import { useState, type ReactNode } from 'react';
import { T, Var, useGT } from 'gt-react';
import { formatDistanceToNow, parseISO } from 'date-fns';
import {
  SparkleIcon,
  ArrowsClockwiseIcon,
  SpinnerIcon,
  PaperPlaneTiltIcon,
  EyeSlashIcon,
  XIcon,
} from '@phosphor-icons/react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { TextInput } from '@/components/common/TextInput';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Action, Subject } from '@erp/authz';
import { useCan } from '@/hooks/useCan';
import {
  getArCollectionActionIcon,
  getArCollectionActionLabel,
  isEmailableActionId,
} from '@/utils/arCollectionActions';
import type {
  DismissedArCollectionAction,
  ResolvedCollectionAction,
  ShortPayRecommendation,
  ShortPayRecommendationConfidence,
  ShortPayRecommendationResult,
} from '@erp/rpc';

/**
 * Shared presentational card for a collection thread's AI action recommendation.
 * Both the short-pay and over-payment cards render this — they differ only in
 * which RPC hook feeds it and the empty-state copy, so the container components
 * call their hook and pass the result down here. Keeps the recommendation UI
 * (summary, ranked actions, confidence, draft-email seeding, dismiss/undismiss,
 * staleness) in one place.
 */

type Salesperson = { name: string; email: string | null } | null;

interface Props {
  /**
   * Open the inline email composer in the thread view, seeded from a specific
   * recommended action. The parent owns the composer and the thread identity.
   */
  onDraftEmail: (seed: {
    initialPrompt: string;
    initialToRecipients?: string[];
  }) => void;
  /** Copy shown when no recommendation has been generated for this thread yet. */
  emptyState: ReactNode;
  recommendation: ShortPayRecommendationResult | null;
  generatedAt: string | null;
  isStale: boolean;
  hasCachedResult: boolean;
  isLoading: boolean;
  regenerate: () => void;
  isRegenerating: boolean;
  salesperson: Salesperson;
  dismissedActions: DismissedArCollectionAction[];
  /**
   * Resolved action catalog (org + per-customer config, scoped to the thread
   * type) used to render each action id's label + "Draft email" affordance.
   * Falls back to known-default/humanized labels for ids not in the catalog.
   */
  actionCatalog: ResolvedCollectionAction[];
  dismissAction: (
    action: DismissedArCollectionAction['action'],
    reason?: string,
  ) => void;
  isDismissing: boolean;
  undismissAction: (noteId: string) => void;
  isUndismissing: boolean;
}

/**
 * Turn a recommendation into a natural-language instruction for the email-draft
 * agent's manual mode. The agent already loads the thread's invoice/payment
 * context itself, so we only need to convey the intent, the why, and the facts
 * the model surfaced. The recommendation's situational summary is included so
 * the draft is grounded in the whole picture, not just one action.
 *
 * The recipient framing depends on the action: `email_sales_person` directs
 * the draft at the named rep (so the model writes "Hi <name>" rather than
 * addressing the customer), while every other emailable action is customer-
 * facing as before.
 */
function buildDraftPrompt(
  rec: ShortPayRecommendation,
  summary: string,
  salesperson: Salesperson,
  label: string,
): string {
  const audience =
    rec.action === 'email_sales_person' && salesperson
      ? `the sales person (${salesperson.name}${salesperson.email ? `, ${salesperson.email}` : ''})`
      : 'the customer';
  const lines = [`Draft a "${label}" email to ${audience} about this thread.`];
  const trimmedSummary = summary.trim();
  if (trimmedSummary.length > 0) {
    lines.push('', `Situation: ${trimmedSummary}`);
  }
  lines.push('', `Reason for this action: ${rec.rationale}`);
  if (rec.evidence.length > 0) {
    lines.push('', 'Supporting facts:');
    for (const ev of rec.evidence) lines.push(`- ${ev}`);
  }
  return lines.join('\n');
}

const CONFIDENCE_STYLES: Record<ShortPayRecommendationConfidence, string> = {
  high: 'border-green-600 text-green-700',
  medium: 'border-amber-500 text-amber-700',
  low: 'border-muted-foreground/40 text-muted-foreground',
};

/**
 * The "don't suggest this again" control: a muted icon button that opens a
 * popover with an optional reason. Confirming mutes the action for the thread's
 * customer (org-wide). Local popover state resets when it closes so the next
 * open starts clean.
 */
function DismissActionPopover({
  actionLabel,
  onConfirm,
  disabled,
}: {
  actionLabel: string;
  onConfirm: (reason?: string) => void;
  disabled: boolean;
}) {
  const gt = useGT();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  function handleOpenChange(next: boolean) {
    setOpen(next);
    if (!next) setReason('');
  }

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 w-7 p-0 text-muted-foreground"
          title={`Don't suggest "${actionLabel}" again`}
          aria-label={`Don't suggest "${actionLabel}" again`}
        >
          <EyeSlashIcon className="h-3.5 w-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-2 p-3">
        <div className="space-y-1">
          <p className="text-sm font-medium">
            <T context="popover title, mute a recommended action">
              Don't suggest this again
            </T>
          </p>
          <p className="text-xs text-muted-foreground">
            <T context="popover help, dismissal applies to whole customer">
              Hides <Var>{actionLabel}</Var> for this customer across all
              threads. You can undo it below.
            </T>
          </p>
        </div>
        <TextInput
          value={reason}
          onValueChange={setReason}
          rows={2}
          maxLength={500}
          placeholder={gt('Reason (optional)', {
            context: 'placeholder, optional reason for muting a recommendation',
          })}
          className="text-xs"
        />
        <div className="flex justify-end gap-2">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs"
            onClick={() => handleOpenChange(false)}
          >
            <T context="button, cancel dismissal">Cancel</T>
          </Button>
          <Button
            size="sm"
            className="h-7 px-2 text-xs"
            disabled={disabled}
            onClick={() => {
              const trimmed = reason.trim();
              onConfirm(trimmed.length > 0 ? trimmed : undefined);
              handleOpenChange(false);
            }}
          >
            <T context="button, confirm dismissal">Don't suggest</T>
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

/**
 * "Hidden for this customer" footer: lists the muted actions with an ✕ to
 * restore each one (which archives the backing memory bank note).
 */
function DismissedActionsFooter({
  dismissedActions,
  onUndismiss,
  disabled,
  canUndismiss,
  getLabel,
}: {
  dismissedActions: DismissedArCollectionAction[];
  onUndismiss: (noteId: string) => void;
  disabled: boolean;
  canUndismiss: boolean;
  getLabel: (actionId: string) => string;
}) {
  if (dismissedActions.length === 0) return null;
  return (
    <div className="border-t pt-2 space-y-1.5">
      <p className="text-xs font-medium text-muted-foreground">
        <T context="footer label, muted recommendation actions">
          Hidden for this customer
        </T>
      </p>
      <div className="flex flex-wrap gap-1.5">
        {dismissedActions.map((dismissed) => (
          <Badge
            key={dismissed.noteId}
            variant="secondary"
            className="gap-1 pr-1 text-[10px] font-normal text-muted-foreground"
            title={dismissed.reason ?? undefined}
          >
            {getLabel(dismissed.action)}
            {canUndismiss && (
              <button
                type="button"
                disabled={disabled}
                onClick={() => onUndismiss(dismissed.noteId)}
                className="rounded-full p-0.5 hover:bg-muted-foreground/20 disabled:opacity-50"
                aria-label={`Restore "${getLabel(dismissed.action)}"`}
                title={`Restore "${getLabel(dismissed.action)}"`}
              >
                <XIcon className="h-3 w-3" />
              </button>
            )}
          </Badge>
        ))}
      </div>
    </div>
  );
}

export function CollectionRecommendationCard({
  onDraftEmail,
  emptyState,
  recommendation,
  generatedAt,
  isStale,
  hasCachedResult,
  isLoading,
  regenerate,
  isRegenerating,
  salesperson,
  dismissedActions,
  actionCatalog,
  dismissAction,
  isDismissing,
  undismissAction,
  isUndismissing,
}: Props) {
  const [canUndismiss] = useCan(Action.Delete, Subject.MemoryBankNote);
  // Resolve an action id to its display label / emailable flag: prefer the
  // server-resolved catalog entry, fall back to known-default/humanized.
  const catalogById = new Map(actionCatalog.map((entry) => [entry.id, entry]));
  const labelFor = (actionId: string) =>
    getArCollectionActionLabel(actionId, catalogById.get(actionId)?.label);
  const emailableFor = (actionId: string) =>
    isEmailableActionId(actionId, catalogById.get(actionId)?.emailable);

  const generatedRelative = generatedAt
    ? (() => {
        const parsed = parseISO(generatedAt);
        return Number.isNaN(parsed.getTime())
          ? null
          : formatDistanceToNow(parsed, { addSuffix: true });
      })()
    : null;

  return (
    <Card>
      <CardHeader className="pb-2 flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="text-sm flex items-center gap-1.5">
          <SparkleIcon className="h-4 w-4 text-primary" />
          <T context="card title, AI recommended collection actions">
            Recommended actions
          </T>
        </CardTitle>
        <Button
          variant="ghost"
          size="sm"
          onClick={regenerate}
          disabled={isRegenerating}
          className="h-7 px-2 text-xs text-muted-foreground"
        >
          {isRegenerating ? (
            <SpinnerIcon className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <ArrowsClockwiseIcon className="h-3.5 w-3.5" />
          )}
          <span className="ml-1">
            <T context="button label, regenerate AI recommendation">
              Regenerate
            </T>
          </span>
        </Button>
      </CardHeader>
      <CardContent className="space-y-3">
        {isLoading ? (
          <div className="space-y-2">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-16 w-full" />
          </div>
        ) : isRegenerating && !recommendation ? (
          <p className="text-sm text-muted-foreground">
            <T context="status, generating recommendation">
              Analyzing the thread evidence…
            </T>
          </p>
        ) : !hasCachedResult || !recommendation ? (
          <p className="text-sm text-muted-foreground">{emptyState}</p>
        ) : (
          <>
            {recommendation.summary && (
              <p className="text-sm text-muted-foreground">
                {recommendation.summary}
              </p>
            )}

            <ul className="space-y-2">
              {recommendation.recommendations.map((rec, idx) => {
                const ActionIcon = getArCollectionActionIcon(rec.action);
                return (
                  <li
                    key={`${rec.action}-${idx}`}
                    className="rounded-lg border bg-card p-2.5 space-y-1.5"
                  >
                    <div className="flex items-center gap-2">
                      <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary/10">
                        <ActionIcon className="h-4 w-4 text-primary" />
                      </div>
                      <span className="text-sm font-semibold truncate">
                        {labelFor(rec.action)}
                      </span>
                      {emailableFor(rec.action) &&
                        // For email_sales_person we need a deliverable
                        // address to seed the TO field — if Famous only
                        // carried a name we hide the button (rather than
                        // open a draft with nowhere to send it) and leave
                        // the rationale visible so the clerk knows why the
                        // action surfaced.
                        (rec.action !== 'email_sales_person' ||
                          salesperson?.email != null) && (
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() =>
                              onDraftEmail({
                                initialPrompt: buildDraftPrompt(
                                  rec,
                                  recommendation.summary,
                                  salesperson,
                                  labelFor(rec.action),
                                ),
                                initialToRecipients:
                                  rec.action === 'email_sales_person' &&
                                  salesperson?.email
                                    ? [salesperson.email]
                                    : undefined,
                              })
                            }
                          >
                            <PaperPlaneTiltIcon className="h-3.5 w-3.5 mr-1" />
                            <T context="button, draft an email from this recommendation">
                              Draft email
                            </T>
                          </Button>
                        )}
                      <Badge
                        variant="outline"
                        className={cn(
                          'ml-auto text-[10px] capitalize',
                          CONFIDENCE_STYLES[rec.confidence],
                        )}
                      >
                        {rec.confidence}
                      </Badge>
                      <DismissActionPopover
                        actionLabel={labelFor(rec.action)}
                        disabled={isDismissing}
                        onConfirm={(reason) =>
                          dismissAction(rec.action, reason)
                        }
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      {rec.rationale}
                    </p>
                    {rec.evidence.length > 0 && (
                      <ul className="list-disc pl-4 text-xs text-muted-foreground/80 space-y-0.5">
                        {rec.evidence.map((ev, i) => (
                          <li key={i}>{ev}</li>
                        ))}
                      </ul>
                    )}
                  </li>
                );
              })}
            </ul>

            {recommendation.recommendations.length === 0 && (
              <p className="text-sm text-muted-foreground">
                {canUndismiss ? (
                  <T context="empty state, every recommended action muted">
                    Every recommended action is hidden for this customer.
                    Restore one below or regenerate.
                  </T>
                ) : (
                  <T context="empty state, every recommended action muted without restore permission">
                    Every recommended action is hidden for this customer.
                  </T>
                )}
              </p>
            )}

            <DismissedActionsFooter
              dismissedActions={dismissedActions}
              onUndismiss={undismissAction}
              disabled={isUndismissing}
              canUndismiss={canUndismiss}
              getLabel={labelFor}
            />

            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              {isStale && (
                <Badge
                  variant="outline"
                  className="border-amber-500 text-amber-700 text-[10px]"
                >
                  <T context="badge, recommendation is stale">
                    Evidence changed
                  </T>
                </Badge>
              )}
              {generatedRelative && (
                <span>
                  <T>
                    Generated <Var>{generatedRelative}</Var>
                  </T>
                </span>
              )}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
